// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `/play/:machineId` (M3-37, G1-06, G1-07, G1-09; 08 §2, §3).
//
// The machine the URL names, with a ball already up: somebody who navigated here chose this
// machine, so making them press a second button to start it is a step that asks nothing.
//
// **An unknown id is a 404 rather than a fallback to NEON NOVA.** Silently serving a different
// machine from the one the URL names is the kind of helpfulness that makes a shared link produce a
// different game for the person who receives it — and now that the Builder exists, a link to
// somebody's deleted machine would quietly become a link to the flagship.
//
// *That sentence used to be the whole of this file.* Until G1 the route held a one-element `Set`
// built from the bundled bytes and answered 404 to everything else, so the rule was enforced by
// there being nothing else to serve. It is now enforced by `machineSource.ts`, which resolves an id
// through bundle → cache → network and answers with a refusal that names itself.
//
// FOUR ANSWERS, AND EACH IS A DIFFERENT PAGE
// -------------------------------------------
// The distinctions are not decoration. A 402 rendered as a 404 tells somebody who could have bought
// the machine that it does not exist; a connection failure rendered as a 404 tells them their link
// is wrong when it is not; and a machine this build cannot run rendered as *anything* generic tells
// its author nothing they can act on, which is why that page carries the validation class that
// refused it.
//
// **The flagship never reaches any of this.** `useMachine` answers it synchronously out of the
// bundle before it names the account chunk, so `/play/neon-nova` renders a machine on its first
// frame and makes no request — FR-030, and 08 §1's "instant NEON NOVA play".

import { useEffect } from 'react'
import { Link, useParams } from 'react-router'
import { CabinetSkeleton, LazyMachineStage } from '../components/LazyMachineStage'
import { useLocalisation } from '../i18n/useLanguage'
import { BUNDLED_MACHINE_ID } from '../machineSource'
import { useMachine } from '../useMachineFeed'
import { clearPageMeta, setPageMeta } from '../pageMeta'
import { machineCopy } from '../../seo/pageCopy'

export function PlayRoute(): React.JSX.Element {
  const { t } = useLocalisation()
  const { machineId } = useParams()
  const { outcome } = useMachine(machineId)

  /**
   * Tell the shell which machine this is (S3-02).
   *
   * **Both these pages used to be called `Play`.** The route table maps one pattern to one
   * message key, and a machine’s name is not in the route table — it is in the definition, which
   * arrives from the bundle, a cache or the network. So `/play/neon-nova` and `/play/iron-fathom`
   * shared a title, a heading and a description, and the sitemap offered two addresses that were
   * one page to a crawler and to a bookmark.
   *
   * The definition is *read* for its own words — `meta.title` and `meta.subtitle`, which every
   * machine carries — and nothing about it changes.
   */
  const path = machineId === undefined ? undefined : `/play/${machineId}`
  const definition = outcome?.kind === 'ok' ? outcome.definition : undefined
  useEffect(() => {
    if (path === undefined) return
    const meta = (definition as { meta?: { title?: unknown; subtitle?: unknown } } | undefined)?.meta
    const title = typeof meta?.title === 'string' ? meta.title.trim() : ''
    if (title === '') return
    const subtitle = typeof meta?.subtitle === 'string' ? meta.subtitle : undefined
    const copy = machineCopy(subtitle === undefined ? { title } : { title, subtitle })
    setPageMeta(path, { title: copy.title, description: copy.description, h1: copy.h1 })
    return () => {
      clearPageMeta(path)
    }
  }, [path, definition])
  // Still resolving. A skeleton rather than nothing: 08 §3 requires a loading state on every
  // control, and an empty page for the second and a half a 60 KB definition takes is the state a
  // player reads as "broken" rather than as "coming".
  if (outcome === undefined) {
    return (
      <div className="page">
        <h1>{t('play.loading', { machineId: machineId ?? '' })}</h1>
        <p className="page__lede" role="status" aria-live="polite">
          {t('play.loading-title')}
        </p>
        {/*
          **The skeleton the stage already had**, not a second one. `.skeleton` is 08 §1's — on
          screen at 0.0 s, saying "a machine is arriving here" in the second before one does,
          because a blank rectangle for a second and a half is indistinguishable from a page that
          has failed. The only new thing is a box to hold it: it is absolutely positioned, so
          without one it would fill the shell.

          A cabinet silhouette rather than a spinner. A spinner says something is happening; a shape
          says what is coming, and it occupies the room the machine will take so nothing jumps.
        */}
        <CabinetSkeleton />
      </div>
    )
  }

  if (outcome.kind === 'ok') return <LazyMachineStage key={outcome.machineId} machine={outcome.definition} />

  const named = outcome.machineId

  if (outcome.reason === 'needs-pro') {
    // **The paywall at the moment of desire**, which is the only moment an upsell is honest: this
    // person has a link to a specific machine and wants that machine. `?want=full-roster` is the
    // same capability id the arcade's locked tiles carry, so the account page resolves the
    // entitlement and shows one offer rather than two pages disagreeing about what is being sold.
    return (
      <div className="page">
        <h1>{t('play.locked-heading', { machineId: named })}</h1>
        <p className="page__lede">{t('play.locked-body')}</p>
        <Link className="button button--primary" to="/account?want=full-roster" title={t('play.locked-title')}>
          {t('play.locked-cta')}
        </Link>
        <Link className="button" to={`/play/${BUNDLED_MACHINE_ID}`} title={t('play.browse-title')}>
          {t('play.play-flagship')}
        </Link>
        {/*
          **Third, and below the price, on purpose** (M10-25).

          This page is the moment of desire and the paid door belongs at the top of it. What the
          Builder adds is the one demonstration of the subscription that costs the reader nothing —
          a person who wanted a machine they cannot have can make one instead, and the workspace
          opens for anybody. It does not undercut the offer above it: designing is free, and
          *keeping* what you design is the same subscription this page is selling.
        */}
        <Link className="button" to="/builder" title={t('builder.open-title')}>
          {t('builder.open')}
        </Link>
      </div>
    )
  }

  if (outcome.reason === 'unreadable') {
    return (
      <div className="page">
        <h1>{t('play.unreadable-heading', { machineId: named })}</h1>
        <p className="page__lede">{t('play.unreadable-body')}</p>
        {/*
          The class that refused it, verbatim — 04 §16's own numbering, which is what the Builder's
          validation panel shows this machine's author. Both surfaces give the same word to the same
          problem, so a player forwarding this line to whoever made the machine is forwarding
          something actionable.
        */}
        <p className="page__detail">{outcome.detail}</p>
        <Link className="button" to="/arcade" title={t('play.browse-title')}>
          {t('play.go-arcade')}
        </Link>
      </div>
    )
  }

  if (outcome.reason === 'offline') {
    return (
      <div className="page">
        <h1>{t('play.offline-heading')}</h1>
        <p className="page__lede">{t('play.offline-body')}</p>
        <Link className="button button--primary" to={`/play/${BUNDLED_MACHINE_ID}`} title={t('play.browse-title')}>
          {t('play.play-flagship')}
        </Link>
        {/*
          A reload rather than a retry button wired to the hook: the recovery for "the arcade could
          not be reached" is the whole page, including the chunk that would not download, and a
          button that re-ran only the fetch would spin for ever against a failure one layer up.
        */}
        <a className="button" href={`/play/${encodeURIComponent(named)}`} title={t('play.retry-title')}>
          {t('play.retry')}
        </a>
      </div>
    )
  }

  return (
    <div className="page">
      <h1>{t('play.not-found-heading', { machineId: named })}</h1>
      <p className="page__lede">{t('play.not-found-body')}</p>
      <Link className="button" to="/arcade" title={t('play.browse-title')}>
        {t('play.go-arcade')}
      </Link>
      {/*
        **The machine-shaped dead end** (M10-25), which is the one `Arcade.tsx` was describing when
        it argued for the build card: *"you look for a machine, you do not find it, you make one."*
        Somebody reading this asked for a specific machine by name and there is none — the strongest
        case in the product for offering to make one, and it was the page with the fewest doors.
      */}
      <Link className="button" to="/builder" title={t('builder.open-title')}>
        {t('builder.open')}
      </Link>
    </div>
  )
}
